/** @jsxImportSource @emotion/react */
import { useState, ChangeEvent, useEffect } from 'react'
import styled from '@emotion/styled'
import { Button, FormControlLabel, IconButton, Switch, Tooltip, Dialog, CircularProgress } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import { ConfirmationDialog } from 'components/partials'
import { StyledFormLabel, StyledInput } from 'components/widgets'
import { PRIMARY_COLOR, getAppColors } from 'lib/constants'
import { Organization, trans, TranslationGroup, TranslationKey } from 'lib/types'
import { rowContainerStyles, titleTextStyles } from 'styles'
import { useGlobal } from 'contexts/GlobalProvider'
import { MappedIn } from 'lib/types/mappedIn'
import { updateOrganization } from 'models/api'
import * as PopUpNotifications from 'models/PopUpNotifications'
import MappedInWizard from 'components/partials/MappedInWizard/MappedInWizard'
import { rssiToDistanceExponential } from 'lib/utils/trilateration'

const DeleteButton = styled(IconButton)({ marginTop: '0 !important', paddingTop: 0, paddingBottom: 0 })
const FormContainer = styled.div({
  position: 'relative',
  display: 'flex',
  padding: '0 2.4rem',
  marginBottom: '5.0rem',
  flexDirection: 'column',
  flex: '0 0 33.3333%',
  borderRight: `1px solid ${getAppColors('--color-gray-2')}`,
  boxSizing: 'border-box',
})
const SpaceBetweenContainer = styled.div({ justifyContent: 'space-between', display: 'flex', flex: 1 })
const StyledButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '8px 20px',
  border: `2px solid ${PRIMARY_COLOR}`,
  boxSizing: 'border-box',
  fontFamily: 'Montserrat',
  boxShadow: '0px 4px 8px rgba(20, 48, 72, 0.1)',
  fontSize: '1.0rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.2rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginRight: '0px',
  cursor: 'pointer',
  borderRadius: '2px',
})
export const SubHeader = styled.h3({
  fontSize: '1.2rem',
  fontWeight: 'bold',
  letterSpacing: '0.1rem',
  lineHeight: '1.5rem',
  color: PRIMARY_COLOR,
  textTransform: 'uppercase',
})
export const SwitchLabel = styled.div({ fontSize: '1.0rem' })
const TitleRowContainer = styled.div({ marginBottom: '5.0rem' })
export const Toggle = styled.div({ flex: 1 })
export const Toggles = styled.div({ display: 'flex' })
export const ToggleSwitch = withStyles({
  switchBase: {
    color: 'white',
    '&$checked': { color: 'rgba(48, 188, 176, 1)' },
    '&$checked + $track': { backgroundColor: 'rgba(48, 188, 176, 0.33)' },
  },
  checked: {},
  track: {},
})(Switch)

type Props = { org: Organization | null }

export function MappedInIntegrationForm({ org }: Props) {
  const translation: TranslationGroup = trans.merge(TranslationKey.SETTINGS)
  const { setSelectedOrg } = useGlobal()

  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [openSetupWizard, setOpenSetupWizard] = useState(false)

  const [mappedIn, setMappedIn] = useState<MappedIn | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (org?.mappedin) {
      setMappedIn(org.mappedin)
    } else {
      setMappedIn(null)
    }
    console.log('~~mappedinObj', mappedIn)
  }, [org])

  useEffect(() => {
    const d = rssiToDistanceExponential(-80, { rssiRef: -63, scale: 1, decayRate: 0.086 })
    console.log('~tri-d', d)
  }, [])

  const handleEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMappedIn({ ...mappedIn, isEnabled: !mappedIn?.isEnabled } as MappedIn)
  }

  const handleFormSubmit = () => {
    if (org) {
      setLoading(true)

      updateOrganization({ ...org, mappedin: mappedIn })
        .then(data => {
          setSelectedOrg(data.data)
          PopUpNotifications.fireSuccess({ content: translation.integration_saved })
        })
        .catch(() => {
          PopUpNotifications.fireError({ content: translation.integration_save_failed })
        })
        .finally(() => setLoading(false))
    }
  }

  return (
    <>
      <ConfirmationDialog
        title={translation.delete_integration}
        description={translation.confirm_delete_integration}
        open={openConfirmModal}
        onConfirm={() => setOpenConfirmModal(false)}
        handleClose={() => setOpenConfirmModal(false)}
      />

      <FormContainer>
        <TitleRowContainer>
          <SpaceBetweenContainer>
            <h3 css={titleTextStyles}>{'MappedIn'}</h3>
          </SpaceBetweenContainer>
        </TitleRowContainer>

        <div css={rowContainerStyles}>
          <Toggles>
            <Toggle>
              <SubHeader>{translation.activate}</SubHeader>
              <FormControlLabel
                control={
                  <ToggleSwitch checked={mappedIn?.isEnabled || false} onChange={handleEnabledChange} name="activate" />
                }
                label={<SwitchLabel>{mappedIn?.isEnabled ? translation.enabled : translation.disabled}</SwitchLabel>}
              />
            </Toggle>
          </Toggles>
        </div>

        <div css={rowContainerStyles}>
          <StyledFormLabel>
            {'Map ID'}
            <StyledInput
              id="mappedin-map"
              required
              disableUnderline
              fullWidth
              placeholder={translation.enter_url}
              value={mappedIn?.mapId}
              onChange={e => setMappedIn({ ...mappedIn, mapId: e.target.value } as MappedIn)}
              inputProps={{ maxLength: 255 }}
            />
          </StyledFormLabel>
        </div>

        <div css={rowContainerStyles}>
          <StyledFormLabel>
            {translation.apiKey}
            <StyledInput
              id="mappedin-key"
              required
              disableUnderline
              fullWidth
              placeholder={translation.enter_apikey}
              value={mappedIn?.key}
              onChange={e => setMappedIn({ ...mappedIn, key: e.target.value } as MappedIn)}
              inputProps={{ maxLength: 255 }}
            />
          </StyledFormLabel>
        </div>

        <div css={rowContainerStyles}>
          <StyledFormLabel>
            {'API SECRET'}
            <StyledInput
              id="mappedin-secret"
              disableUnderline
              fullWidth
              placeholder={translation.enter_property_code}
              value={mappedIn?.secret}
              onChange={e => setMappedIn({ ...mappedIn, secret: e.target.value } as MappedIn)}
              inputProps={{ maxLength: 255 }}
            />
          </StyledFormLabel>
        </div>

        <div css={rowContainerStyles} style={{ alignItems: 'center', display: 'flex', gap: '1rem' }}>
          <StyledButton onClick={() => null}>{translation.cancel}</StyledButton>
          <StyledButton onClick={handleFormSubmit} disabled={loading}>
            {loading ? <CircularProgress size={18} style={{ marginRight: 8 }} /> : null}
            {translation.apply_changes}
          </StyledButton>
        </div>

        <div
          css={rowContainerStyles}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '2rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            flexWrap: 'nowrap',
          }}
        >
          <StyledButton style={{ padding: '12px 8px' }} onClick={() => setOpenSetupWizard(true)}>
            🚀 Launch Setup Wizard
          </StyledButton>
        </div>

        <Dialog fullWidth maxWidth="md" open={openSetupWizard} onClose={() => setOpenSetupWizard(false)}>
          <div style={{ padding: '2rem', position: 'relative' }}>
            <IconButton
              onClick={() => setOpenSetupWizard(false)}
              style={{ position: 'absolute', top: 8, right: 8 }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <MappedInWizard onClose={() => setOpenSetupWizard(false)} org={org} mappedIn={mappedIn} />
          </div>
        </Dialog>
      </FormContainer>
    </>
  )
}
